import { API_BASE_URL, resolvePublicFileUrl, API_EDU_PREFIX, API_CONFIG, CreditCost, ROLE_BONUS_CONFIG, EDU_CONFIG, UI_CONFIG, AD_CONFIG, PAYMENT_CONFIG, HOMEPAGE_CONFIG, GUIDE_CONFIG, LEGAL_CONFIG, SEO_CONFIG, SEO_STATIC_SNAPSHOT, CONTACT_CONFIG, FOOTER_CONFIG, FEATURE_FLAGS } from "../config";
import { updatePageSEO } from "../utils/seoUtils";

/**
 * Tên sự kiện được dispatch lên `window` khi `configService` lấy config từ backend xong
 * (thành công, lỗi, hoặc dùng default đều fire). Component có thể lắng nghe để biết
 * khi nào nội dung động (LEGAL_CONFIG, FOOTER_CONFIG, …) đã sẵn sàng để render.
 */
export const CONFIG_SERVICE_READY_EVENT = 'configservice:ready';

/**
 * Service to fetch and manage dynamic system configurations from the backend.
 */
class ConfigService {
    private config: Record<string, any> = {};
    private isInitialized: boolean = false;
    private isInitializing: boolean = false;

    /** True khi `initialize()` đã chạy xong (kể cả nhánh fail → fallback default). */
    public get isReady(): boolean {
        return this.isInitialized;
    }
    private hasKey(key: string): boolean {
        return Object.prototype.hasOwnProperty.call(this.config, key);
    }
    private asBool(value: any): boolean {
        return value === '1' || value === 1 || value === true;
    }
    private asNumber(value: any, fallback: number): number {
        const num = Number(value);
        return Number.isFinite(num) ? num : fallback;
    }
    private nonEmptyString(value: unknown): boolean {
        return value != null && String(value).trim() !== '';
    }

    /** Gộp SEO theo từng path: admin chỉ ghi đè trường có nội dung; không làm mất description/keywords từ bundle. */
    private mergeSeoPages(
        snap: Record<string, { title?: string; description?: string; keywords?: string }>,
        api: Record<string, { title?: string; description?: string; keywords?: string }>
    ): Record<string, { title?: string; description?: string; keywords?: string }> {
        const out: Record<string, { title?: string; description?: string; keywords?: string }> = { ...snap };
        for (const [path, patch] of Object.entries(api || {})) {
            if (!patch || typeof patch !== 'object') continue;
            const base = snap[path] || {};
            const merged: { title?: string; description?: string; keywords?: string } = { ...base, ...patch };
            for (const k of ['title', 'description', 'keywords'] as const) {
                const v = merged[k];
                if (v != null && String(v).trim() === '') {
                    merged[k] = base[k];
                }
            }
            out[path] = merged;
        }
        return out;
    }

    async initialize(): Promise<void> {
        if (this.isInitialized || this.isInitializing) return Promise.resolve();
        this.isInitializing = true;

        try {
            const url = `${API_BASE_URL}${API_EDU_PREFIX}/system/config?t=${Date.now()}`;
            const response = await fetch(url, {
                headers: { 
                    Accept: "application/json",
                    "Cache-Control": "no-cache"
                },
            });
            const text = await response.text();
            let data: { success?: boolean; config?: Record<string, unknown> } = {};
            try {
                data = JSON.parse(text) as typeof data;
            } catch {
                const snippet = text.slice(0, 240).replace(/\s+/g, " ");
                console.error(
                    "[ConfigService] Config endpoint returned non-JSON (often PHP/nginx error HTML or an unproxied /api). URL:",
                    url,
                    "Status:",
                    response.status,
                    "Snippet:",
                    snippet,
                );
                if (snippet.includes("open_basedir")) {
                    console.warn(
                        "[ConfigService] PHP open_basedir đang giới hạn chỉ trong …/public/ — Laravel cần đọc storage/, vendor/, bootstrap/ (vd: file_exists maintenance.php). Trên site backend (be…), đặt open_basedir = /www/wwwroot/<tên-site-backend>/:/tmp/ (thư mục gốc chứa composer.json), sửa cả .user.ini nếu có, restart PHP-FPM. Chi tiết: docs/ULTIMATE_DEPLOYMENT_GUIDE_2026.md",
                    );
                }
                return;
            }

            if (!response.ok || data.success === false) {
                if (!response.ok) {
                    console.warn("[ConfigService] Config HTTP", response.status, "— using defaults.");
                } else {
                    console.warn("[ConfigService] Backend reported success=false — using defaults.");
                }
                return;
            }

            this.config = data.config || {};

            // Mutate global constants with backend values
            this.applyToGlobals();

            if (typeof window !== "undefined") {
                updatePageSEO(window.location.pathname);
            }

            this.isInitialized = true;
        } catch (error) {
            console.error("[ConfigService] Failed to load config from backend, using defaults.", error);
            // Vẫn đánh dấu ready để UI không kẹt skeleton vô tận khi backend down
            this.isInitialized = true;
        } finally {
            this.isInitializing = false;
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent(CONFIG_SERVICE_READY_EVENT));
            }
        }
    }

    private applyToGlobals() {
        // AI Config
        if (this.hasKey('ai_model_default')) {
            API_CONFIG.GEMINI_TEXT_MODEL = this.config.ai_model_default || '';
        }
        if (this.hasKey('ai_model_html5')) {
            API_CONFIG.GEMINI_HTML5_MODEL = String(this.config.ai_model_html5 || '').trim() || API_CONFIG.GEMINI_HTML5_MODEL;
        }
        if (this.hasKey('openai_model_text')) {
            API_CONFIG.OPENAI_TEXT_MODEL = this.config.openai_model_text || '';
        }
        if (this.hasKey('openai_model_image')) API_CONFIG.OPENAI_MODEL_IMAGE = this.config.openai_model_image || '';

        if (this.hasKey('anthropic_model_text')) {
            const m = String(this.config.anthropic_model_text || '').trim();
            if (m) API_CONFIG.ANTHROPIC_TEXT_MODEL = m;
        }

        if (this.hasKey('ai_model_pricing')) {
            try {
                const raw = this.config.ai_model_pricing;
                const parsed = (raw === '' || raw === null)
                    ? {}
                    : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    API_CONFIG.MODEL_PRICING = parsed as typeof API_CONFIG.MODEL_PRICING;
                }
            } catch (_) {
                console.warn('[ConfigService] ai_model_pricing không phải JSON hợp lệ — dùng mặc định.');
            }
        }

        if (this.hasKey('ai_pipeline_routing')) {
            try {
                const raw = this.config.ai_pipeline_routing;
                const parsed = (raw === '' || raw === null)
                    ? {}
                    : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    API_CONFIG.PIPELINE_ROUTING = parsed as typeof API_CONFIG.PIPELINE_ROUTING;
                }
            } catch (_) {
                console.warn('[ConfigService] ai_pipeline_routing không phải JSON hợp lệ — dùng mặc định.');
            }
        }

        if (this.hasKey('ai_provider_default')) {
            const provider = this.config.ai_provider_default || 'gemini';
            Object.keys(API_CONFIG.DEFAULT_PROVIDERS).forEach(key => {
                API_CONFIG.DEFAULT_PROVIDERS[key] = provider;
            });
        }

        // Credit Costs
        if (this.hasKey('cost_quiz')) CreditCost.QUIZ = this.asNumber(this.config.cost_quiz, CreditCost.QUIZ);
        if (this.hasKey('cost_game')) CreditCost.GAME = this.asNumber(this.config.cost_game, CreditCost.GAME);
        if (this.hasKey('cost_homework')) CreditCost.HOMEWORK = this.asNumber(this.config.cost_homework, CreditCost.HOMEWORK);
        if (this.hasKey('cost_lesson_plan')) CreditCost.LESSON_PLAN = this.asNumber(this.config.cost_lesson_plan, CreditCost.LESSON_PLAN);
        if (this.hasKey('cost_image')) CreditCost.IMAGE = this.asNumber(this.config.cost_image, CreditCost.IMAGE);
        if (this.hasKey('cost_presentation')) CreditCost.PRESENTATION = this.asNumber(this.config.cost_presentation, CreditCost.PRESENTATION);
        if (this.hasKey('cost_video_scene')) CreditCost.VIDEO = this.asNumber(this.config.cost_video_scene, CreditCost.VIDEO);
        if (this.hasKey('cost_sample_data')) CreditCost.SAMPLE_DATA = this.asNumber(this.config.cost_sample_data, CreditCost.SAMPLE_DATA);
        if (this.hasKey('cost_segmentation')) CreditCost.SEGMENTATION = this.asNumber(this.config.cost_segmentation, CreditCost.SEGMENTATION);
        if (this.hasKey('cost_skkn_section')) CreditCost.SKKN_SECTION = this.asNumber(this.config.cost_skkn_section, CreditCost.SKKN_SECTION);
        if (this.hasKey('cost_music_quick')) CreditCost.MUSIC_QUICK = this.asNumber(this.config.cost_music_quick, CreditCost.MUSIC_QUICK);
        if (this.hasKey('cost_music_enterprise')) CreditCost.MUSIC_ENTERPRISE = this.asNumber(this.config.cost_music_enterprise, CreditCost.MUSIC_ENTERPRISE);

        // Payment
        if (this.config.payment_manual_proof_note_required !== undefined) {
            PAYMENT_CONFIG.manualProofNoteRequired = this.asBool(this.config.payment_manual_proof_note_required);
        }

        // Bank Transfer
        PAYMENT_CONFIG.methods.bankTransfer = {
            enabled: this.hasKey('payment_bank_enabled') ? this.asBool(this.config.payment_bank_enabled) : PAYMENT_CONFIG.methods.bankTransfer.enabled,
            bankName: this.hasKey('payment_bank_name') ? (this.config.payment_bank_name || '') : PAYMENT_CONFIG.methods.bankTransfer.bankName,
            accountNumber: this.hasKey('payment_bank_account_number') ? (this.config.payment_bank_account_number || '') : PAYMENT_CONFIG.methods.bankTransfer.accountNumber,
            accountName: this.hasKey('payment_bank_account_name') ? (this.config.payment_bank_account_name || '') : PAYMENT_CONFIG.methods.bankTransfer.accountName,
            qrCodeUrl: this.hasKey('payment_qr_code_template') ? (this.config.payment_qr_code_template || '') : PAYMENT_CONFIG.methods.bankTransfer.qrCodeUrl,
        };

        // PayOS
        PAYMENT_CONFIG.methods.payos = {
            enabled: this.hasKey('payment_automation_enabled') ? this.asBool(this.config.payment_automation_enabled) : PAYMENT_CONFIG.methods.payos.enabled
        };

        // VNPay
        PAYMENT_CONFIG.methods.vnpay = { 
            enabled: this.hasKey('vnpay_enabled') ? this.asBool(this.config.vnpay_enabled) : PAYMENT_CONFIG.methods.vnpay.enabled, 
            merchantId: this.hasKey('vnpay_merchant_id') ? (this.config.vnpay_merchant_id || '') : PAYMENT_CONFIG.methods.vnpay.merchantId
        };
        
        // Momo
        PAYMENT_CONFIG.methods.momo = {
            enabled: this.hasKey('payment_momo_enabled') ? this.asBool(this.config.payment_momo_enabled) : PAYMENT_CONFIG.methods.momo.enabled,
            phone: this.hasKey('payment_momo_phone') ? (this.config.payment_momo_phone || '') : PAYMENT_CONFIG.methods.momo.phone,
            name: this.hasKey('payment_momo_name') ? (this.config.payment_momo_name || '') : PAYMENT_CONFIG.methods.momo.name,
            qrCodeUrl: this.hasKey('payment_momo_qr_template') ? (this.config.payment_momo_qr_template || '') : PAYMENT_CONFIG.methods.momo.qrCodeUrl,
        };

        // Bonuses
        if (this.hasKey('bonus_teacher')) ROLE_BONUS_CONFIG.TEACHER = this.asNumber(this.config.bonus_teacher, ROLE_BONUS_CONFIG.TEACHER);
        if (this.hasKey('bonus_parent')) ROLE_BONUS_CONFIG.PARENT = this.asNumber(this.config.bonus_parent, ROLE_BONUS_CONFIG.PARENT);
        if (this.hasKey('bonus_student')) ROLE_BONUS_CONFIG.STUDENT = this.asNumber(this.config.bonus_student, ROLE_BONUS_CONFIG.STUDENT);

        // UI & Ads
        if (this.hasKey('app_name')) UI_CONFIG.APP_NAME = this.config.app_name || '';
        if (this.hasKey('app_subtitle')) UI_CONFIG.APP_SUBTITLE = this.config.app_subtitle || '';
        if (this.hasKey('app_logo_url')) {
            const url = this.config.app_logo_url || '';
            UI_CONFIG.APP_LOGO_URL = url ? (url.startsWith('http') ? url : resolvePublicFileUrl(url.startsWith('/') ? url : '/' + url)) : '';
        }
        if (this.config.ui_show_app_name_in_header !== undefined) {
            UI_CONFIG.SHOW_APP_NAME_IN_HEADER = this.asBool(this.config.ui_show_app_name_in_header);
        }
        if (this.config.ui_show_credit_costs !== undefined) {
            UI_CONFIG.SHOW_CREDIT_COSTS = this.asBool(this.config.ui_show_credit_costs);
        }
        if (this.config.enable_ai_selector !== undefined) {
            API_CONFIG.ENABLE_AI_SELECTOR = this.asBool(this.config.enable_ai_selector);
        }
        if (this.config.ai_provider_selectable !== undefined && this.config.ai_provider_selectable !== null && typeof this.config.ai_provider_selectable === "object") {
            const s = this.config.ai_provider_selectable as Record<string, unknown>;
            API_CONFIG.AI_PROVIDER_SELECTABLE = {
                gemini: this.asBool(s.gemini),
                openai: this.asBool(s.openai),
                anthropic: this.asBool(s.anthropic),
            };
        }
        if (this.hasKey('ui_credit_label')) UI_CONFIG.CREDIT_LABEL = this.config.ui_credit_label || '';
        if (this.hasKey('dashboard_greetings')) {
            try {
                const raw = this.config.dashboard_greetings;
                const parsed = (raw === '' || raw === null) ? null : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                UI_CONFIG.DASHBOARD_GREETINGS = parsed && typeof parsed === 'object' ? parsed : null;
            } catch (_) { UI_CONFIG.DASHBOARD_GREETINGS = null; }
        }

        // Legal content
        if (this.hasKey('legal_terms_of_use_content')) LEGAL_CONFIG.TERMS_OF_USE_HTML = this.config.legal_terms_of_use_content || '';
        if (this.hasKey('legal_privacy_policy_content')) LEGAL_CONFIG.PRIVACY_POLICY_HTML = this.config.legal_privacy_policy_content || '';

        // SEO — merge lên bản tĩnh từ code; ô trống trên admin = giữ mặc định bundle
        const snap = SEO_STATIC_SNAPSHOT;
        if (this.hasKey('seo_default_title')) {
            SEO_CONFIG.DEFAULT_TITLE = this.nonEmptyString(this.config.seo_default_title)
                ? String(this.config.seo_default_title)
                : snap.DEFAULT_TITLE;
        }
        if (this.hasKey('seo_default_description')) {
            SEO_CONFIG.DEFAULT_DESCRIPTION = this.nonEmptyString(this.config.seo_default_description)
                ? String(this.config.seo_default_description)
                : snap.DEFAULT_DESCRIPTION;
        }
        if (this.hasKey('seo_default_keywords')) {
            SEO_CONFIG.DEFAULT_KEYWORDS = this.nonEmptyString(this.config.seo_default_keywords)
                ? String(this.config.seo_default_keywords)
                : snap.DEFAULT_KEYWORDS;
        }
        if (this.hasKey('seo_pages')) {
            try {
                const raw = this.config.seo_pages;
                const parsed = (raw === '' || raw === null) ? {} : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                const apiPages = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, { title?: string; description?: string; keywords?: string }> : {};
                SEO_CONFIG.PAGES = this.mergeSeoPages(snap.PAGES, apiPages);
            } catch (_) {
                SEO_CONFIG.PAGES = { ...snap.PAGES };
            }
        }
        if (this.hasKey('ui_records_per_page')) UI_CONFIG.RECORDS_PER_PAGE = this.asNumber(this.config.ui_records_per_page, UI_CONFIG.RECORDS_PER_PAGE);
        if (this.config.affiliate_cash_enabled !== undefined) {
            UI_CONFIG.AFFILIATE_CASH_ENABLED = this.asBool(this.config.affiliate_cash_enabled);
        }
        if (this.hasKey('feature_video_generation')) {
            FEATURE_FLAGS.VIDEO_GENERATION = this.asBool(this.config.feature_video_generation);
        }
        if (this.hasKey('feature_image_generation')) {
            FEATURE_FLAGS.IMAGE_GENERATION = this.asBool(this.config.feature_image_generation);
        }
        if (this.hasKey('feature_music_generation')) {
            FEATURE_FLAGS.MUSIC_GENERATION = this.asBool(this.config.feature_music_generation);
        }
        if (this.hasKey('feature_music_enterprise_mode')) {
            FEATURE_FLAGS.MUSIC_ENTERPRISE = this.asBool(this.config.feature_music_enterprise_mode);
        }
        if (this.hasKey('feature_music_lyria')) {
            FEATURE_FLAGS.MUSIC_LYRIA = this.asBool(this.config.feature_music_lyria);
        }
        if (this.hasKey('feature_share_credits')) {
            FEATURE_FLAGS.SHARE_CREDITS = this.asBool(this.config.feature_share_credits);
        }
        if (this.hasKey('ad_min_display_time_ms')) AD_CONFIG.MIN_DISPLAY_TIME = this.asNumber(this.config.ad_min_display_time_ms, AD_CONFIG.MIN_DISPLAY_TIME);
        else if (this.hasKey('ui_ad_min_display_time_ms')) AD_CONFIG.MIN_DISPLAY_TIME = this.asNumber(this.config.ui_ad_min_display_time_ms, AD_CONFIG.MIN_DISPLAY_TIME);

        // Homepage
        if (this.config.homepage_video_source === 'server' || this.config.homepage_video_source === 'youtube') {
            HOMEPAGE_CONFIG.VIDEO_SOURCE = this.config.homepage_video_source;
        }
        if (this.hasKey('homepage_youtube_video_id')) HOMEPAGE_CONFIG.YOUTUBE_VIDEO_ID = this.config.homepage_youtube_video_id || '';
        if (this.hasKey('homepage_video_url')) HOMEPAGE_CONFIG.VIDEO_URL = this.config.homepage_video_url || '';
        if (this.hasKey('homepage_hero_title')) HOMEPAGE_CONFIG.HERO_TITLE = this.config.homepage_hero_title || '';
        if (this.hasKey('homepage_hero_subtitle')) HOMEPAGE_CONFIG.HERO_SUBTITLE = this.config.homepage_hero_subtitle || '';
        if (this.hasKey('homepage_hero_mockup_image')) HOMEPAGE_CONFIG.HERO_MOCKUP_IMAGE = this.config.homepage_hero_mockup_image || '';
        if (this.hasKey('homepage_hero_badge_value')) HOMEPAGE_CONFIG.HERO_BADGE_VALUE = this.config.homepage_hero_badge_value || '';
        if (this.hasKey('homepage_hero_badge_label')) HOMEPAGE_CONFIG.HERO_BADGE_LABEL = this.config.homepage_hero_badge_label || '';
        if (this.hasKey('homepage_video_preview_title')) HOMEPAGE_CONFIG.VIDEO_PREVIEW_TITLE = this.config.homepage_video_preview_title || '';
        if (this.hasKey('homepage_video_preview_subtitle')) HOMEPAGE_CONFIG.VIDEO_PREVIEW_SUBTITLE = this.config.homepage_video_preview_subtitle || '';
        if (this.hasKey('homepage_stats')) {
            try {
                const raw = this.config.homepage_stats;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                HOMEPAGE_CONFIG.STATS = Array.isArray(parsed) ? parsed : [];
            } catch (_) { HOMEPAGE_CONFIG.STATS = []; }
        }
        if (this.hasKey('homepage_promo_title')) HOMEPAGE_CONFIG.PROMO_TITLE = this.config.homepage_promo_title || '';
        if (this.hasKey('homepage_promo_description')) HOMEPAGE_CONFIG.PROMO_DESCRIPTION = this.config.homepage_promo_description || '';
        if (this.hasKey('homepage_teacher_section_title')) HOMEPAGE_CONFIG.TEACHER_SECTION_TITLE = this.config.homepage_teacher_section_title || '';
        if (this.hasKey('homepage_teacher_section_description')) HOMEPAGE_CONFIG.TEACHER_SECTION_DESCRIPTION = this.config.homepage_teacher_section_description || '';
        if (this.hasKey('homepage_student_section_title')) HOMEPAGE_CONFIG.STUDENT_SECTION_TITLE = this.config.homepage_student_section_title || '';
        if (this.hasKey('homepage_student_section_description')) HOMEPAGE_CONFIG.STUDENT_SECTION_DESCRIPTION = this.config.homepage_student_section_description || '';
        if (this.hasKey('homepage_teacher_social_caption')) HOMEPAGE_CONFIG.TEACHER_SOCIAL_CAPTION = this.config.homepage_teacher_social_caption || '';
        if (this.hasKey('homepage_student_social_caption')) HOMEPAGE_CONFIG.STUDENT_SOCIAL_CAPTION = this.config.homepage_student_social_caption || '';
        if (this.hasKey('homepage_teacher_social_avatars')) {
            try {
                const raw = this.config.homepage_teacher_social_avatars;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                HOMEPAGE_CONFIG.TEACHER_SOCIAL_AVATARS = Array.isArray(parsed) ? parsed.filter((u: unknown) => typeof u === 'string' && String(u).trim().length > 0) : [];
            } catch (_) {
                HOMEPAGE_CONFIG.TEACHER_SOCIAL_AVATARS = [];
            }
        }
        if (this.hasKey('homepage_student_social_avatars')) {
            try {
                const raw = this.config.homepage_student_social_avatars;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                HOMEPAGE_CONFIG.STUDENT_SOCIAL_AVATARS = Array.isArray(parsed) ? parsed.filter((u: unknown) => typeof u === 'string' && String(u).trim().length > 0) : [];
            } catch (_) {
                HOMEPAGE_CONFIG.STUDENT_SOCIAL_AVATARS = [];
            }
        }
        if (this.hasKey('homepage_feature_showcase')) {
            try {
                const raw = this.config.homepage_feature_showcase;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                if (Array.isArray(parsed) && parsed.length > 0) HOMEPAGE_CONFIG.FEATURE_SHOWCASE = parsed;
            } catch (_) {}
        }
        if (this.hasKey('homepage_teacher_tools_media')) {
            try {
                const raw = this.config.homepage_teacher_tools_media;
                const parsed = (raw === '' || raw === null) ? {} : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                if (parsed && typeof parsed === 'object') HOMEPAGE_CONFIG.TEACHER_TOOLS_MEDIA = parsed;
            } catch (_) {}
        }
        if (this.hasKey('homepage_student_tools_media')) {
            try {
                const raw = this.config.homepage_student_tools_media;
                const parsed = (raw === '' || raw === null) ? {} : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                if (parsed && typeof parsed === 'object') HOMEPAGE_CONFIG.STUDENT_TOOLS_MEDIA = parsed;
            } catch (_) {}
        }

        // Guide onboarding popup
        if (this.config.guide_video_source === 'server' || this.config.guide_video_source === 'youtube') {
            GUIDE_CONFIG.VIDEO_SOURCE = this.config.guide_video_source;
        }
        if (this.hasKey('guide_youtube_video_id')) GUIDE_CONFIG.YOUTUBE_VIDEO_ID = this.config.guide_youtube_video_id || '';
        if (this.hasKey('guide_video_url')) GUIDE_CONFIG.VIDEO_URL = this.config.guide_video_url || '';
        if (this.hasKey('guide_title')) GUIDE_CONFIG.TITLE = this.config.guide_title || '';
        if (this.hasKey('guide_content')) GUIDE_CONFIG.CONTENT = this.config.guide_content || '';

        // Contact
        if (this.config.contact_email !== undefined) CONTACT_CONFIG.EMAIL = this.config.contact_email || '';
        if (this.config.contact_hotline !== undefined) CONTACT_CONFIG.HOTLINE = String(this.config.contact_hotline || '');
        if (this.config.contact_address !== undefined) CONTACT_CONFIG.ADDRESS = this.config.contact_address || '';
        if (this.config.contact_zalo_link !== undefined) CONTACT_CONFIG.ZALO_LINK = this.config.contact_zalo_link || '';
        if (this.config.contact_facebook_link !== undefined) CONTACT_CONFIG.FACEBOOK_LINK = this.config.contact_facebook_link || '';
        if (this.config.contact_messenger_link !== undefined) CONTACT_CONFIG.MESSENGER_LINK = this.config.contact_messenger_link || '';

        // Footer
        if (this.config.footer_creative_title !== undefined) FOOTER_CONFIG.CREATIVE_TITLE = this.config.footer_creative_title || '';
        if (this.config.footer_learning_title !== undefined) FOOTER_CONFIG.LEARNING_TITLE = this.config.footer_learning_title || '';
        if (this.config.footer_support_title !== undefined) FOOTER_CONFIG.SUPPORT_TITLE = this.config.footer_support_title || '';
        if (this.config.footer_copyright !== undefined) FOOTER_CONFIG.COPYRIGHT = this.config.footer_copyright || '';
        if (this.config.footer_description !== undefined) FOOTER_CONFIG.DESCRIPTION = this.config.footer_description || '';
        if (this.config.footer_tax_info !== undefined) FOOTER_CONFIG.TAX_INFO = this.config.footer_tax_info || '';
        
        if (this.config.footer_creative_links !== undefined) {
            try {
                const raw = this.config.footer_creative_links;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                FOOTER_CONFIG.CREATIVE_LINKS = Array.isArray(parsed) ? parsed : [];
            } catch (_) { FOOTER_CONFIG.CREATIVE_LINKS = []; }
        }
        if (this.config.footer_learning_links !== undefined) {
            try {
                const raw = this.config.footer_learning_links;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                FOOTER_CONFIG.LEARNING_LINKS = Array.isArray(parsed) ? parsed : [];
            } catch (_) { FOOTER_CONFIG.LEARNING_LINKS = []; }
        }
        if (this.config.footer_support_links !== undefined) {
            try {
                const raw = this.config.footer_support_links;
                const parsed = (raw === '' || raw === null) ? [] : (typeof raw === 'string' ? JSON.parse(raw) : raw);
                FOOTER_CONFIG.SUPPORT_LINKS = Array.isArray(parsed) ? parsed : [];
            } catch (_) { FOOTER_CONFIG.SUPPORT_LINKS = []; }
        }
        if (this.config.footer_crafted_by !== undefined) FOOTER_CONFIG.CRAFTED_BY = this.config.footer_crafted_by || '';

        // Other limits & configs
        if (this.hasKey('limit_max_parents_per_student')) EDU_CONFIG.MAX_PARENTS_PER_STUDENT = this.asNumber(this.config.limit_max_parents_per_student, EDU_CONFIG.MAX_PARENTS_PER_STUDENT);
        if (this.hasKey('limit_max_classes_free')) EDU_CONFIG.DEFAULT_LIMITS.MAX_CLASSES = this.asNumber(this.config.limit_max_classes_free, EDU_CONFIG.DEFAULT_LIMITS.MAX_CLASSES);
        if (this.hasKey('limit_max_students_free')) EDU_CONFIG.DEFAULT_LIMITS.MAX_STUDENTS = this.asNumber(this.config.limit_max_students_free, EDU_CONFIG.DEFAULT_LIMITS.MAX_STUDENTS);
        if (this.hasKey('min_credits_onetime_purchase')) EDU_CONFIG.DEFAULT_LIMITS.MIN_CREDITS_ONETIME_PURCHASE = this.asNumber(this.config.min_credits_onetime_purchase, EDU_CONFIG.DEFAULT_LIMITS.MIN_CREDITS_ONETIME_PURCHASE);
    }

    get(key: string, defaultValue: any = null): any {
        return this.config[key] ?? defaultValue;
    }
}

export const configService = new ConfigService();
